/* eslint-disable react-hooks/exhaustive-deps */
import { Formik } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { AsyncTypeahead } from 'react-bootstrap-typeahead'
import { useLocation } from 'react-router-dom'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'
import { BatchAddEditSchema } from '../../../utils/validationSchema'
import CustomButton from '../../Button/CustomButton'
import CustomCloseButton from '../../Button/CustomCloseButton'
import DatePickerComponent from '../../Input/DatePicker'
import TextField from '../../Input/TextInput'
import { convertDateWithTime } from '../../../helpers/constants'
import { FileText, X, CloudUpload } from 'lucide-react'

function ModalAddBatch({
  show,
  onHide,
  onAdd,
  item,
  batch,
  setBatchId = undefined,
  setInputBatch = undefined
}) {
  const { pathname } = useLocation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const refItem = useRef(AsyncTypeahead)
  const filterItem = () => true
  const [isLoadingItem, setIsLoadingItem] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(function () {
    if (pathname === '/master/batch') {
      fetchItems()
    }
  }, [])

  useEffect(() => {
    if (show) {
      setPreviewFile(null)
    }
  }, [show])

  const minSelectableDate = new Date()
  minSelectableDate.setDate(minSelectableDate.getDate() + 1)

  async function fetchItems(query) {
    try {
      const res = await api.get(`master/items/all`, { params: { q: query } })
      const results = res.data
      setItems(results.data)
    } catch (error) {
      const errMsg =
        error?.response?.data?.errors?.message || 'An error occurred'

      Common.showPopUpErrorMessage(errMsg)
    }
  }

  const handleFileUpload = async (file, batchNumber) => {
    if (!batchNumber) {
      Common.showPopUpErrorMessage('Please enter batch number first')
      return
    }

    try {
      setIsUploading(true)
      const formData = new FormData()
      formData.append('batch', batchNumber.toUpperCase())
      formData.append('file', file)

      const response = await api.post(
        '/inbound/pr-draft/upload-coa',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )

      if (response.data.success) {
        const uploadedData = response.data.data

        setPreviewFile({
          name: uploadedData?.name || file.name,
          contentId: uploadedData?.contentId || ''
        })
        Common.showPopUpSuccessMessage('COA uploaded successfully')
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.errors?.message || 'Failed to upload COA'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (values) => {
    try {
      setLoading(true)
      const payload = {
        item_id: values.item_id,
        batch: values.batch.toUpperCase(),
        manufacturing_date: values.manufacturing_date,
        exp_date: values.exp_date,
        note: values.note || ''
      }

      const response = await api.post('/master/batch', payload)

      const { data } = response
      if (data.success) {
        onHide()
        if (setBatchId) {
          setBatchId(data.data.id)
        }
        if (setInputBatch) {
          setInputBatch({
            batch: data.data.batch,
            exp_date: data.data.exp_date,
            id: data.data.id,
            manufacturing_date: values.manufacturing_date,
            newBatch: true,
            document_id: previewFile?.contentId || null,
            document_name: previewFile?.name || null
          })
        }
        Common.showPopUpSuccessMessage(response?.data?.message)
        if (onAdd) onAdd()
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }

  async function handleSearchItem(query) {
    try {
      setIsLoadingItem(true)

      if (query.length >= 3) {
        fetchItems(query)
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsLoadingItem(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Formik
        initialValues={{
          item_id: item ? item.id : '',
          exp_date: '',
          batch: batch ? batch.batch : '',
          manufacturing_date: '',
          note: ''
        }}
        validateOnChange={true}
        enableReinitialize={true}
        validationSchema={BatchAddEditSchema}
        onSubmit={(values) => {
          values.manufacturing_date = convertDateWithTime(
            values.manufacturing_date,
            'YYYY-MM-DD'
          )
          values.exp_date = convertDateWithTime(values.exp_date, 'YYYY-MM-DD')
          handleSubmit(values)
        }}
      >
        {({
          handleBlur,
          handleSubmit,
          handleChange,
          setFieldValue,
          values,
          errors,
          touched,
          isValid,
          dirty,
          setFieldTouched
        }) => {
          return (
            <Form className="d-grid gap-2" onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Add Batch</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row>
                  <Col md={12}>
                    <Form.Label
                      className="text-muted fw-bold text-uppercase"
                      style={{ fontSize: '13px', letterSpacing: '0.5px' }}
                    >
                      <small>ITEM</small>
                    </Form.Label>
                    {!item ? (
                      <>
                        <AsyncTypeahead
                          id="basic-typeahead-single"
                          labelKey="item_name"
                          isLoading={isLoadingItem}
                          filterBy={filterItem}
                          onSearch={handleSearchItem}
                          onChange={(e) => {
                            setFieldValue('item_id', e.length ? e[0].id : '')
                          }}
                          value={values.item_id}
                          options={items}
                          placeholder="Choose a item ..."
                          ref={refItem}
                          isInvalid={errors.item_id}
                          style={{ height: '45px' }}
                        />
                        {errors.item_id && (
                          <Form.Control.Feedback type="invalid">
                            {errors.item_id}
                          </Form.Control.Feedback>
                        )}
                      </>
                    ) : (
                      <Form.Control
                        id="item"
                        name="item"
                        className="mb-1 mt-1"
                        type="text"
                        placeholder={item.name}
                        value={item.name}
                        onBlur={handleBlur}
                        style={{ marginTop: '-10px', height: '45px' }}
                        disabled={item ? true : false}
                      />
                    )}
                  </Col>

                  <Col md={6} className="mt-3">
                    <TextField
                      label="batch"
                      name="batch"
                      value={
                        batch
                          ? batch.batch.toUpperCase()
                          : values.batch.toUpperCase()
                      }
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={batch ? true : false}
                      placeholder="Enter Batch"
                      styles={{ height: '45px' }}
                    />
                  </Col>
                  <DatePickerComponent
                    label="manufacturing date"
                    name="manufacturing_date"
                    value={values.manufacturing_date}
                    handleChange={setFieldValue}
                    handleBlur={() =>
                      setFieldTouched('manufacturing_date', true)
                    }
                    errors={errors}
                    touched={touched}
                    col={6}
                    required
                    styles={{ height: '45px' }}
                  />
                  <DatePickerComponent
                    label="Exp. Date"
                    name="exp_date"
                    required
                    value={values.exp_date}
                    minDate={minSelectableDate}
                    handleChange={setFieldValue}
                    handleBlur={() => setFieldTouched('exp_date', true)}
                    errors={errors}
                    touched={touched}
                    col={6}
                    styles={{ height: '45px' }}
                  />
                  <DatePickerComponent
                    label="date patch"
                    name="date_patch"
                    value={values.date_patch}
                    handleChange={setFieldValue}
                    handleBlur={() => setFieldTouched('date_patch', true)}
                    errors={errors}
                    touched={touched}
                    col={6}
                    required={false}
                    disabled={true}
                  />
                  <Col md={12} className="mt-1">
                    <TextField
                      label="note"
                      name="note"
                      value={values.note}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      placeholder="Enter Note"
                      as="textarea"
                      styles={{ height: '45px' }}
                    />
                  </Col>
                  <Col md={12} className="mt-3">
                    <Form.Group>
                      <Form.Label
                        className="text-muted fw-bold text-uppercase"
                        style={{ fontSize: '13px', letterSpacing: '0.5px' }}
                      >
                        <small>COA {isUploading && '(Uploading...)'}</small>
                      </Form.Label>

                      {!previewFile ? (
                        <div
                          className={`custom-file-upload ${isUploading ? 'opacity-50' : ''}`}
                        >
                          <input
                            type="file"
                            disabled={isUploading}
                            onChange={(event) => {
                              const file = event.currentTarget.files[0]
                              if (file) {
                                handleFileUpload(
                                  file,
                                  values.batch || batch?.batch
                                )
                              }
                            }}
                            accept=".pdf"
                          />
                          <div className="upload-content">
                            <CloudUpload className="upload-icon" size={24} />
                            <span className="upload-text">
                              {isUploading ? 'Uploading...' : 'Upload File'}
                            </span>
                            <span className="upload-hint">PDF</span>
                          </div>
                        </div>
                      ) : (
                        <div className="file-preview-card d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <FileText
                              className="text-secondary me-2"
                              size={18}
                            />
                            <span
                              className="text-dark fw-bold text-truncate"
                              style={{ maxWidth: '250px', fontSize: '0.9rem' }}
                            >
                              {previewFile.name}
                            </span>
                          </div>
                          <div
                            onClick={() => setPreviewFile(null)}
                            className="remove-file-btn"
                            title="Remove file"
                          >
                            <X size={16} />
                          </div>
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <CustomCloseButton text="Cancel" onClick={onHide} />
                <CustomButton
                  onClick={handleSubmit}
                  variant={
                    !isValid || !dirty || loading ? 'secondary' : 'warning'
                  }
                  disabled={!isValid || !dirty || loading}
                  text={!loading ? 'Add' : 'Loading...'}
                  classNameValue={`text-white ${
                    !isValid || !dirty || loading ? 'opacity-25' : ''
                  }`}
                />
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default ModalAddBatch
